// Reusable Project Filters Component
// Flexible filter component that renders based on configuration props
// Can be used across ProjectManagementPage, ViewAllProjectsPage, and PracticeProjectsBrowsePage

import { Search } from 'lucide-react';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';

// Configuration interface - controls which filters to show
export interface FilterConfig {
  showSearch?: boolean;
  showCourse?: boolean;
  showInstructor?: boolean;
  showLinked?: boolean;
  showStatus?: boolean;
  showDifficulty?: boolean;
  showType?: boolean;
  showTags?: boolean;
}

// Main props interface
export interface ProjectFiltersProps {
  // Filter values
  searchQuery?: string;
  courseFilter?: string;
  createdBy?: string;
  isLinkedFilter?: string;
  statusFilter?: string;
  difficultyFilter?: string;
  typeFilter?: string;
  tags?: string[];

  // Callbacks
  onSearchChange?: (value: string) => void;
  onCourseChange?: (value: string | undefined) => void;
  onCreatedByChange?: (value: string | undefined) => void;
  onIsLinkedChange?: (value: string | undefined) => void;
  onStatusChange?: (value: string | undefined) => void;
  onDifficultyChange?: (value: string | undefined) => void;
  onTypeChange?: (value: string | undefined) => void;
  onTagsChange?: (tags: string[]) => void;

  // Configuration - controls which filters are rendered
  config: FilterConfig;

  // Data for dropdowns
  courses?: Array<{ id: string; contextTitle: string; code?: string; name?: string }>;
  coursesLoading?: boolean;
  staffData?: Array<{ id: string; name: string }>;
  staffLoading?: boolean;

  // Custom labels/placeholders
  searchPlaceholder?: string;
  courseLabel?: string;
  instructorLabel?: string;

  // Styling
  className?: string;
  inputValue?: string;
  handleSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function ProjectFilters({
  // Filter values
  searchQuery = '',
  courseFilter,
  createdBy,
  isLinkedFilter,
  statusFilter,
  difficultyFilter,
  typeFilter,
  tags = [],

  // Callbacks
  onSearchChange,
  onCourseChange,
  onCreatedByChange,
  onIsLinkedChange,
  onStatusChange,
  onDifficultyChange,
  onTypeChange,
  onTagsChange,

  // Configuration
  config = {},

  // Data
  courses = [],
  coursesLoading = false,
  staffData = [],
  staffLoading = false,

  // Custom labels
  searchPlaceholder = 'Search...',
  courseLabel = 'All courses',
  instructorLabel = 'Select Instructors',

  // Styling
  className = '',
  inputValue,
  handleSearchChange,
}: ProjectFiltersProps) {
  const {
    showSearch = true,
    showCourse = false,
    showInstructor = false,
    showLinked = false,
    showStatus = false,
    showDifficulty = false,
    showType = false,
    showTags = false,
  } = config;

  return (
    <div className={`flex flex-wrap items-center gap-4 ${className}`}>
      {/* Search Input */}
      {showSearch && (
        <div className="relative flex-1 min-w-40 w-80">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 bg-bits-grey-100" />
          <Input
            placeholder={searchPlaceholder}
            className="pl-10 bg-bits-grey-100 text-bits-grey-600"
            value={inputValue !== undefined ? inputValue : searchQuery}
            onChange={
              handleSearchChange ||
              ((e) => onSearchChange?.(e.target.value))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
          />
        </div>
      )}

      {/* Course Filter */}
      {showCourse && onCourseChange && (
        <Select
          value={courseFilter || 'all-courses'}
          onValueChange={(v) =>
            onCourseChange(v === 'all-courses' ? undefined : v)
          }
        >
          <SelectTrigger className="w-full sm:w-auto sm:min-w-40 bg-bits-grey-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="text-md">
            <SelectItem value="all-courses">{courseLabel}</SelectItem>
            {coursesLoading ? (
              <SelectItem value="loading" disabled>
                Loading courses...
              </SelectItem>
            ) : (
              courses.map((course) => (
                <SelectItem key={course.id} value={course.id}>
                  {course.contextTitle || course.name || course.code}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      )}

      {/* Instructor/Created By Filter */}
      {showInstructor && onCreatedByChange && (
        <Select
          value={createdBy || 'all-creators'}
          onValueChange={(v) =>
            onCreatedByChange(v === 'all-creators' ? undefined : v)
          }
        >
          <SelectTrigger className="w-full sm:w-40 bg-bits-grey-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="text-md">
            <SelectItem value="all-creators">{instructorLabel}</SelectItem>
            {!courseFilter ? (
              <SelectItem value="loading" disabled>
                Please select a course
              </SelectItem>
            ) : staffLoading ? (
              <SelectItem value="loading" disabled>
                Loading instructors...
              </SelectItem>
            ) : (
              staffData?.map((staff) => (
                <SelectItem key={staff.id} value={staff.id}>
                  {staff.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      )}

      {/* Linked Filter */}
      {showLinked && onIsLinkedChange && (
        <Select
          value={isLinkedFilter || 'all'}
          onValueChange={(v) =>
            onIsLinkedChange(v === 'all' ? undefined : v)
          }
        >
          <SelectTrigger className="w-full sm:w-40 bg-bits-grey-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            <SelectItem value="linked">Linked projects</SelectItem>
            <SelectItem value="unlinked">Unlinked projects</SelectItem>
          </SelectContent>
        </Select>
      )}

      {/* Status Filter */}
      {showStatus && onStatusChange && (
        <Select
          value={statusFilter || 'all-status'}
          onValueChange={(v) =>
            onStatusChange(v === 'all-status' ? undefined : v)
          }
        >
          <SelectTrigger className="w-full sm:w-40 bg-bits-grey-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all-status">All status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      )}

      {/* Difficulty Filter */}
      {showDifficulty && onDifficultyChange && (
        <Select
          value={difficultyFilter || 'all-difficulties'}
          onValueChange={(v) =>
            onDifficultyChange(v === 'all-difficulties' ? undefined : v)
          }
        >
          <SelectTrigger className="w-full sm:w-40 bg-bits-grey-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all-difficulties">All Difficulties</SelectItem>
            <SelectItem value="beginner">Beginner</SelectItem>
            <SelectItem value="intermediate">Intermediate</SelectItem>
            <SelectItem value="advanced">Advanced</SelectItem>
          </SelectContent>
        </Select>
      )}

      {/* Type Filter */}
      {showType && onTypeChange && (
        <Select
          value={typeFilter || 'all-types'}
          onValueChange={(v) =>
            onTypeChange(v === 'all-types' ? undefined : v)
          }
        >
          <SelectTrigger className="w-full sm:w-40 bg-bits-grey-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all-types">All Types</SelectItem>
            <SelectItem value="group">Group</SelectItem>
            <SelectItem value="individual">Individual</SelectItem>
          </SelectContent>
        </Select>
      )}

      {/* Tags Display (if needed) */}
      {showTags && tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, index) => (
            <Badge key={index} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
